import React, { useState, useEffect, useRef, useCallback } from 'react';

// Utility function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

interface PurchaseFormData {
  medicine_name: string;
  medicine_company: string;
  medicine_composition: string;
  batch_no: string;
  expiry_date: string;
  quantity: number;
  quantity_type: string;
  tablets_per_strip?: number;
  unit_cost: number;
  selling_price: number;
  mrp: number;
  hsn_code: string;
  storage_location: string;
}

interface QuickPurchaseProps {
  onClose: () => void;
}

// Medicine suggestion interface
interface MedicineSuggestion {
  Name: string;
  Manufacturer: string;
  Salt_Composition: string;
  Quantity: string;
  Image_URL: string;
  key: string;
  relevance_score: number;
}

// Quantity Type options
const QUANTITY_TYPES = [
  'None',
  'Strip',
  'Bottles',
  'Syrup',
  'Tablet',
  'Box',
  'Tube',
  'Powder',
  'Vial',
  'Syringe',
  'Packet',
  'Pouch',
  'Capsule',
  'Individual',
  'Piece',
  'Sachets'
];

// Helper function to format currency
const formatCurrency = (value: number): string => {
  return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Helper function to format expiry date input as user types (auto-adds "-" separators for MM-YYYY, DD defaults to 01)
const formatExpiryDateInput = (value: string): string => {
  if (!value) return '';
  
  // Allow digits, "-", and "/" (for backward compatibility, convert "/" to "-")
  let cleaned = value.replace(/[^\d/-]/g, '').replace(/\//g, '-');
  
  // Don't allow "-" at the start
  if (cleaned.startsWith('-')) {
    cleaned = cleaned.substring(1);
  }
  
  // Split by "-" to handle month and year parts (user only enters MM-YYYY)
  const parts = cleaned.split('-');
  let monthPart = parts[0] || '';
  let yearPart = parts[1] || '';
  
  // Remove extra parts if more than 2 parts exist (ignore day if user accidentally enters it)
  if (parts.length > 2) {
    // If user entered DD-MM-YYYY, ignore DD and use MM-YYYY
    if (parts[0].length === 2 && parts[1].length === 2) {
      monthPart = parts[1] || '';
      yearPart = parts[2] || '';
    } else {
      monthPart = parts[0] || '';
      yearPart = parts.slice(1).join('') || '';
    }
  }
  
  // If user has typed digits without separators, auto-insert separator after month
  // Format: MM-YYYY (e.g., "122025" -> "12-2025")
  if (parts.length === 1 && cleaned.length > 0) {
    const digits = cleaned.replace(/-/g, '');
    if (digits.length <= 2) {
      // Just month part
      monthPart = digits;
    } else if (digits.length <= 6) {
      // Month + year
      monthPart = digits.substring(0, 2);
      yearPart = digits.substring(2);
    } else {
      // Too many digits, limit to MM-YYYY
      monthPart = digits.substring(0, 2);
      yearPart = digits.substring(2, 6);
    }
  }
  
  // Validate and limit month to valid range (01-12)
  if (monthPart.length > 0) {
    // Limit month to 2 digits maximum first
    if (monthPart.length > 2) {
      const extra = monthPart.substring(2);
      monthPart = monthPart.substring(0, 2);
      if (!yearPart) {
        yearPart = extra;
      }
    }
    
    // Validate month value - only validate when month is complete (2 digits)
    if (monthPart.length === 2) {
      const monthNum = parseInt(monthPart, 10);
      // If month > 12, limit to 12
      if (monthNum > 12) {
        monthPart = '12';
      } else if (monthNum === 0) {
        // If user enters "00", change to "01"
        monthPart = '01';
      }
    }
    // For single digit, allow user to type freely (will be validated when second digit is entered)
  }
  
  // Limit year to 4 digits maximum
  if (yearPart.length > 4) {
    yearPart = yearPart.substring(0, 4);
  }
  
  // Reconstruct the value with proper separators (MM-YYYY format)
  if (yearPart) {
    // Full date: MM-YYYY
    return `${monthPart}-${yearPart}`;
  } else if (monthPart) {
    // Partial: MM
    return monthPart;
  } else {
    return '';
  }
};

// Helper function to convert YYYY-MM-DD to MM-YYYY format for display (DD always defaults to 01)
const convertToDateFormat = (value: string): string => {
  if (!value || value.trim() === '') return '';
  
  // If already in YYYY-MM-DD format, convert to MM-YYYY (user only sees MM-YYYY)
  const yyyyMmDdMatch = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (yyyyMmDdMatch) {
    const [, year, month] = yyyyMmDdMatch;
    return `${month}-${year}`;
  }
  
  // If already in MM-YYYY format, return as-is
  if (/^\d{2}-\d{4}$/.test(value.trim())) {
    return value.trim();
  }
  
  // If in DD-MM-YYYY format, extract MM-YYYY
  const ddMmYyyyMatch = value.trim().match(/^\d{2}-(\d{2})-(\d{4})$/);
  if (ddMmYyyyMatch) {
    const [, month, year] = ddMmYyyyMatch;
    return `${month}-${year}`;
  }
  
  // If in YYYY-MM format, convert to MM-YYYY
  if (/^\d{4}-\d{2}$/.test(value.trim())) {
    const [year, month] = value.trim().split('-');
    return `${month}-${year}`;
  }
  
  // If in MM/YYYY format (backward compatibility), convert to MM-YYYY
  if (/^\d{2}\/\d{4}$/.test(value.trim())) {
    return value.trim().replace('/', '-');
  }
  
  return '';
};

// Normalize various date formats into YYYY-MM-DD format (DD always defaults to 01)
const parseAndNormalizeDate = (value: string): string => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';

  // Handle MM-YYYY format (primary format - user only enters MM-YYYY)
  const mmYyyyMatch = trimmed.match(/^(\d{1,2})-(\d{4})$/);
  if (mmYyyyMatch) {
    const month = Math.min(12, Math.max(1, Number(mmYyyyMatch[1])));
    const year = Number(mmYyyyMatch[2]);
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }

  // Handle YYYY-MM-DD format (already normalized)
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  // Handle DD-MM-YYYY format - extract MM-YYYY and set DD to 01
  const ddMmYyyyMatch = trimmed.match(/^\d{2}-(\d{1,2})-(\d{4})$/);
  if (ddMmYyyyMatch) {
    const month = Math.min(12, Math.max(1, Number(ddMmYyyyMatch[1])));
    const year = Number(ddMmYyyyMatch[2]);
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }

  // Handle YYYY-MM format - set date to 01
  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    return `${trimmed}-01`;
  }

  // Handle MM/YYYY format (backward compatibility)
  const mmYyyySlashMatch = trimmed.match(/^(\d{1,2})\/(\d{4})$/);
  if (mmYyyySlashMatch) {
    const month = Math.min(12, Math.max(1, Number(mmYyyySlashMatch[1])));
    const year = Number(mmYyyySlashMatch[2]);
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }

  // Handle MM/YY format (backward compatibility)
  const shortMatch = trimmed.match(/^(\d{1,2})[/-](\d{2})$/);
  if (shortMatch) {
    const month = Math.min(12, Math.max(1, Number(shortMatch[1])));
    let year = Number(shortMatch[2]);
    if (year < 100) {
      year += 2000; // assume 2000s for two-digit year
    }
    return `${year}-${String(month).padStart(2, '0')}-01`;
  }

  // Already ISO format YYYY-MM-DD - ensure date is 01
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const match = trimmed.match(/^(\d{4}-\d{2})/);
    if (match) {
      return `${match[1]}-01`;
    }
    return trimmed;
  }

  // Fallback: try native Date parse
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
  }

  return '';
};

// Helper function to create empty row
const createEmptyRow = (): PurchaseFormData => ({
  medicine_name: '',
  medicine_company: '',
  medicine_composition: '',
  batch_no: '',
  expiry_date: '',
  quantity: 0,
  quantity_type: 'None',
  tablets_per_strip: 10,
  unit_cost: 0,
  selling_price: 0,
  mrp: 0,
  hsn_code: '',
  storage_location: ''
});

const QuickPurchase: React.FC<QuickPurchaseProps> = ({ onClose }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showTipBlock, setShowTipBlock] = useState(false);
  const [rows, setRows] = useState<PurchaseFormData[]>([createEmptyRow()]);
  const [removingRows, setRemovingRows] = useState<Set<number>>(new Set());
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableScrollableRef = useRef<HTMLDivElement | null>(null);
  
  // Track display values for expiry date fields to preserve MM-YYYY format during typing
  const [expiryDisplayValues, setExpiryDisplayValues] = useState<{ [key: string]: string }>({});
  
  // Medicine search state
  const [medicineSuggestions, setMedicineSuggestions] = useState<{ [key: number]: MedicineSuggestion[] }>({});
  const [showDropdown, setShowDropdown] = useState<{ [key: number]: boolean }>({});
  const [activeDropdownRow, setActiveDropdownRow] = useState<number | null>(null);
  const [isSearching, setIsSearching] = useState<{ [key: number]: boolean }>({});
  const dropdownRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const searchTimeoutRef = useRef<{ [key: number]: NodeJS.Timeout | null }>({});

  // Column definitions
  const columns: (keyof PurchaseFormData)[] = [
    'medicine_name',
    'batch_no',
    'expiry_date',
    'quantity',
    'quantity_type',
    'tablets_per_strip',
    'unit_cost',
    'mrp',
    'selling_price',
    'hsn_code',
    'storage_location',
    'medicine_company',
    'medicine_composition'
  ];

  // Column display names
  const columnDisplayNames: { [key: string]: string } = {
    medicine_name: 'Medicine Name',
    medicine_company: 'Company',
    medicine_composition: 'Composition',
    batch_no: 'Batch No',
    expiry_date: 'Expiry Date',
    quantity: 'Quantity',
    quantity_type: 'Qty Type',
    tablets_per_strip: 'Tablets/Strip',
    unit_cost: 'Unit Cost',
    selling_price: 'Selling Price',
    mrp: 'MRP',
    hsn_code: 'HSN Code',
    storage_location: 'Storage'
  };

  // Right-aligned columns (numeric fields)
  const rightAlignColumns: (keyof PurchaseFormData)[] = [
    'quantity',
    'tablets_per_strip',
    'unit_cost',
    'selling_price',
    'mrp'
  ];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Close dropdown if clicking outside
      if (activeDropdownRow !== null && dropdownRefs.current[activeDropdownRow]) {
        if (!dropdownRefs.current[activeDropdownRow]?.contains(target)) {
          setShowDropdown(prev => ({ ...prev, [activeDropdownRow]: false }));
          setActiveDropdownRow(null);
        }
      }
    };

    if (activeDropdownRow !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeDropdownRow]);

  // Add new row
  const handleAddRow = () => {
    setRows(prevRows => [...prevRows, createEmptyRow()]);
  };

  // Remove row
  const handleRemoveRow = (rowIndex: number) => {
    setRemovingRows(prev => new Set(prev).add(rowIndex));
    
    setTimeout(() => {
      setRows(prevRows => {
        if (prevRows.length === 1) {
          setRemovingRows(new Set());
          return [createEmptyRow()];
        }
        const updatedRows = prevRows.filter((_, index) => index !== rowIndex);
        setRemovingRows(new Set());
        return updatedRows;
      });
    }, 300);
  };

  // Search medicines function - calls API
  const searchMedicines = async (query: string, rowIndex: number): Promise<MedicineSuggestion[]> => {
    if (!query || query.trim().length === 0) {
      return [];
    }
    
    try {
      const authToken = getAuthToken();
      
      if (!authToken) {
        console.error('Authentication token not found');
        return [];
      }

      // Construct API URL
      const apiUrl = `https://pharma-amd-0-1-42.onrender.com/pharma/api/search?name=${encodeURIComponent(query)}`;
      
      // Call the search API
      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        console.error('Failed to search medicines:', response.status);
        return [];
      }

      const data = await response.json();
      
      // Handle different possible response formats
      let medicines: any[] = [];
      if (data.medicines && Array.isArray(data.medicines)) {
        medicines = data.medicines;
      } else if (Array.isArray(data)) {
        medicines = data;
      } else if (data.data && Array.isArray(data.data)) {
        medicines = data.data;
      } else {
        console.warn('Unknown response format:', Object.keys(data));
        return [];
      }

      // Map API response to MedicineSuggestion format
      const mappedMedicines = medicines.map((medicine: any) => ({
        Name: medicine.Name || '',
        Manufacturer: medicine.Manufacturer || '',
        Salt_Composition: medicine.Salt_Composition || '',
        Quantity: medicine.Quantity || '',
        Image_URL: medicine.Image_URL || '',
        key: medicine.key || `medicine-${medicine.Name || Date.now()}-${rowIndex}`,
        relevance_score: medicine.relevance_score || 0
      }));
    
      return mappedMedicines;
    } catch (error: any) {
      console.error('Error searching medicines:', error);
      return [];
    }
  };

  // Handle medicine name change with autocomplete
  const handleMedicineNameChange = (rowIndex: number, value: string) => {
    // Update the medicine name
    handleCellChange(rowIndex, 'medicine_name', value);

    // Clear previous timeout
    if (searchTimeoutRef.current[rowIndex]) {
      clearTimeout(searchTimeoutRef.current[rowIndex]);
    }

    // Trim and check if we should search
    const trimmedValue = value.trim();
    
    if (trimmedValue.length === 0) {
      setMedicineSuggestions(prev => ({ ...prev, [rowIndex]: [] }));
      setShowDropdown(prev => ({ ...prev, [rowIndex]: false }));
      setIsSearching(prev => ({ ...prev, [rowIndex]: false }));
      return;
    }

    // Set searching state
    setIsSearching(prev => ({ ...prev, [rowIndex]: true }));
    setShowDropdown(prev => ({ ...prev, [rowIndex]: true }));
    setActiveDropdownRow(rowIndex);

    // Debounce search - wait 300ms after user stops typing
    searchTimeoutRef.current[rowIndex] = setTimeout(async () => {
      const suggestions = await searchMedicines(trimmedValue, rowIndex);
      setMedicineSuggestions(prev => ({ ...prev, [rowIndex]: suggestions }));
      setIsSearching(prev => ({ ...prev, [rowIndex]: false }));
      
      // Show dropdown if we have suggestions
      if (suggestions.length > 0) {
        setShowDropdown(prev => ({ ...prev, [rowIndex]: true }));
      }
    }, 300);
  };

  // Handle medicine selection from dropdown
  const handleMedicineSelect = (rowIndex: number, medicine: MedicineSuggestion) => {
    const updatedRows = [...rows];
    const row = { ...updatedRows[rowIndex] };
    
    // Auto-fill fields from selected medicine
    row.medicine_name = medicine.Name || '';
    row.medicine_company = medicine.Manufacturer || '';
    row.medicine_composition = medicine.Salt_Composition || '';
    
    updatedRows[rowIndex] = row;
    setRows(updatedRows);
    
    // Close dropdown
    setShowDropdown(prev => ({ ...prev, [rowIndex]: false }));
    setActiveDropdownRow(null);
    setMedicineSuggestions(prev => ({ ...prev, [rowIndex]: [] }));
  };

  // Handle submit
  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Filter out empty rows
    const validRows = rows.filter(row => 
      row.medicine_name.trim() !== '' && 
      row.quantity > 0
    );

    if (validRows.length === 0) {
      alert('Please add at least one item with Medicine Name and Quantity > 0');
      return;
    }

    // Validate all rows
    for (const row of validRows) {
      if (row.quantity_type === 'Strip' && (!row.tablets_per_strip || row.tablets_per_strip <= 0)) {
        alert(`Row with "${row.medicine_name || 'Unnamed'}" requires Tablets Per Strip when quantity type is strip`);
        return;
      }
    }

    setIsSubmitting(true);
    
    try {
      const authToken = getAuthToken();
      const results: Array<{ success: boolean; data: any; item: string }> = [];
      
      // Submit all items
      for (const item of validRows) {
        const payload = {
          medicine_name: item.medicine_name,
          medicine_company: item.medicine_company || '',
          medicine_composition: item.medicine_composition || '',
          batch_no: item.batch_no || '',
          expiry_date: item.expiry_date || '',
          quantity: item.quantity,
          quantity_type: item.quantity_type,
          ...(item.quantity_type === 'Strip' && item.tablets_per_strip ? { tablets_per_strip: item.tablets_per_strip } : {}),
          unit_cost: item.unit_cost || 0,
          selling_price: item.selling_price || 0,
          mrp: item.mrp || 0,
          hsn_code: item.hsn_code || '',
          storage_location: item.storage_location || ''
        };

        const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/', {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
            ...(authToken && { 'Authorization': `Bearer ${authToken}` })
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error occurred' }));
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        results.push({ success: true, data, item: item.medicine_name });
      }

      console.log('All purchases submitted successfully:', results);
      
      // Show success notification
      setShowSuccessNotification(true);
      
      // Reset form
      setRows([createEmptyRow()]);
      
      // Auto-hide notification and close after 3 seconds
      setTimeout(() => {
        setShowSuccessNotification(false);
        setTimeout(() => {
          onClose();
        }, 300);
      }, 3000);
    } catch (error: any) {
      console.error('Error submitting purchase:', error);
      alert(`Failed to submit purchase: ${error.message || 'Unknown error occurred'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Keyboard shortcuts: 
  // Ctrl/Cmd + Right/Left Arrow: Scroll table horizontally
  // Ctrl/Cmd + A: Add new row
  // Ctrl/Cmd + D: Delete last row
  // Ctrl/Cmd + S: Submit purchase
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
      if (event.ctrlKey || event.metaKey) {
        // Only work on desktop (not mobile)
        if (isMobile) return;

        const key = event.key.toLowerCase();

        // Ctrl+A: Add new row
        if (key === 'a') {
          event.preventDefault();
          event.stopPropagation();
          handleAddRow();
          return;
        }

        // Ctrl+D: Delete last row
        if (key === 'd') {
          event.preventDefault();
          event.stopPropagation();
          if (rows.length > 0) {
            handleRemoveRow(rows.length - 1);
          }
          return;
        }

        // Ctrl+S: Submit purchase
        if (key === 's') {
          event.preventDefault();
          event.stopPropagation();
          if (!isSubmitting) {
            handleSubmit();
          }
          return;
        }

        // Ctrl+Right/Left Arrow: Scroll table horizontally
        if (key === 'arrowright' || key === 'arrowleft') {
          // Try to scroll the inner scrollable div first, then the outer container
          const scrollAmount = 300;
          let scrolled = false;

          // Try inner scrollable div
          if (tableScrollableRef.current) {
            const currentScroll = tableScrollableRef.current.scrollLeft;
            const maxScroll = tableScrollableRef.current.scrollWidth - tableScrollableRef.current.clientWidth;
            
            if (key === 'arrowright' && currentScroll < maxScroll) {
              event.preventDefault();
              event.stopPropagation();
              const targetScroll = Math.min(currentScroll + scrollAmount, maxScroll);
              tableScrollableRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
              });
              scrolled = true;
            } else if (key === 'arrowleft' && currentScroll > 0) {
              event.preventDefault();
              event.stopPropagation();
              const targetScroll = Math.max(currentScroll - scrollAmount, 0);
              tableScrollableRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
              });
              scrolled = true;
            }
          }

          // If inner div didn't scroll, try outer container
          if (!scrolled && tableContainerRef.current) {
            const currentScroll = tableContainerRef.current.scrollLeft;
            const maxScroll = tableContainerRef.current.scrollWidth - tableContainerRef.current.clientWidth;
            
            if (key === 'arrowright' && currentScroll < maxScroll) {
              event.preventDefault();
              event.stopPropagation();
              const targetScroll = Math.min(currentScroll + scrollAmount, maxScroll);
              tableContainerRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
              });
            } else if (key === 'arrowleft' && currentScroll > 0) {
              event.preventDefault();
              event.stopPropagation();
              const targetScroll = Math.max(currentScroll - scrollAmount, 0);
              tableContainerRef.current.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
              });
            }
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isMobile, rows.length, isSubmitting, handleAddRow, handleRemoveRow, handleSubmit]);

  // Handle cell change
  const handleCellChange = (rowIndex: number, field: keyof PurchaseFormData, value: string | number) => {
    const updatedRows = [...rows];
    const row = { ...updatedRows[rowIndex] };
    
    if (field === 'quantity' || field === 'tablets_per_strip' || field === 'unit_cost' || 
        field === 'selling_price' || field === 'mrp') {
      let numValue: number;
      
      if (typeof value === 'number') {
        numValue = value;
      } else if (typeof value === 'string') {
        // Handle string input more carefully
        const trimmedValue = value.trim();
        if (trimmedValue === '') {
          numValue = 0;
        } else {
          // Parse the string value to number, preserving decimal places
          numValue = parseFloat(trimmedValue);
          if (isNaN(numValue)) {
            numValue = 0;
          }
        }
      } else {
        numValue = 0;
      }
      
      (row[field] as number) = numValue;
      
      // Auto-fill selling price with MRP if selling price is empty and MRP is being set
      if (field === 'mrp' && numValue > 0) {
        // Direct, immediate auto-fill
        row.selling_price = numValue;
      }
    } else if (field === 'quantity_type') {
      (row[field] as string) = String(value);
      if (value !== 'Strip') {
        row.tablets_per_strip = undefined;
      } else if (!row.tablets_per_strip) {
        row.tablets_per_strip = 10;
      }
    } else if (field === 'expiry_date') {
      // Special handling for Expiry Date - auto-format input as user types
      const rawValue = String(value || '');
      
      // Format the input (auto-add "-" separators for MM-YYYY)
      const formattedValue = formatExpiryDateInput(rawValue);
      
      // Always store the formatted value in display state for immediate feedback
      setExpiryDisplayValues(prev => ({
        ...prev,
        [`${rowIndex}-expiry_date`]: formattedValue
      }));
      
      // If format is complete (MM-YYYY), also normalize it immediately
      if (/^\d{2}-\d{4}$/.test(formattedValue.trim())) {
        const normalized = parseAndNormalizeDate(formattedValue);
        (row[field] as string) = normalized;
      } else {
        // For partial input, store as-is for now
        (row[field] as string) = formattedValue;
      }
    } else {
      (row[field] as string) = String(value);
    }
    
    updatedRows[rowIndex] = row;
    setRows(updatedRows);
  };

  // Calculate total value
  const calculateTotalValue = (): number => {
    return rows.reduce((sum, row) => sum + ((row.unit_cost || 0) * (row.quantity || 0)), 0);
  };

  return (
    <>
      {/* Success Notification */}
      {showSuccessNotification && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10000,
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          padding: isMobile ? '1rem 1.5rem' : '1.25rem 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)',
          animation: 'slideDownFromTop 0.5s ease-out',
          fontSize: isMobile ? '0.9rem' : '1rem',
          fontWeight: '600',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <svg width={isMobile ? "20" : "24"} height={isMobile ? "20" : "24"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 12l2 2 4-4"/>
            <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
          </svg>
          <span>Purchase submitted successfully!</span>
          <button
            onClick={() => {
              setShowSuccessNotification(false);
              setTimeout(() => {
                onClose();
              }, 300);
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              borderRadius: '50%',
              width: isMobile ? '24px' : '28px',
              height: isMobile ? '24px' : '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              marginLeft: 'auto',
              transition: 'all 0.2s ease',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <svg width={isMobile ? "14" : "16"} height={isMobile ? "14" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      <div style={{
        background: 'white',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '1rem' : '2rem',
        width: '100%',
        overflowY: 'auto',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? '1rem' : '1.5rem',
          paddingBottom: isMobile ? '0.75rem' : '1rem',
          borderBottom: '2px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.75rem' : '1rem' }}>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '1.25rem',
                color: '#6b7280',
                cursor: 'pointer',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.color = '#1f2937';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              <svg width={isMobile ? "18" : "20"} height={isMobile ? "18" : "20"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
            <h2 style={{
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              fontWeight: '700',
              color: '#1f2937',
              margin: 0
            }}>
              Quick Purchase
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={() => setShowTipBlock(!showTipBlock)}
              style={{
                background: showTipBlock ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'transparent',
                border: showTipBlock ? 'none' : '1px solid #d1d5db',
                color: showTipBlock ? 'white' : '#6b7280',
                cursor: 'pointer',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? '32px' : '36px',
                height: isMobile ? '32px' : '36px',
                borderRadius: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!showTipBlock) {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                  e.currentTarget.style.color = '#1f2937';
                }
              }}
              onMouseLeave={(e) => {
                if (!showTipBlock) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }
              }}
              title="Keyboard Shortcuts"
            >
              <svg width={isMobile ? "18" : "20"} height={isMobile ? "18" : "20"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4M12 8h.01"/>
              </svg>
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: isMobile ? '1.25rem' : '1.5rem',
                color: '#6b7280',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? '28px' : '32px',
                height: isMobile ? '28px' : '32px',
                borderRadius: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.color = '#1f2937';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Tip Block */}
        {showTipBlock && (
          <div style={{
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
            border: '1px solid #93c5fd',
            borderRadius: '12px',
            padding: isMobile ? '1rem' : '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '0.75rem'
            }}>
              <h3 style={{
                fontSize: isMobile ? '1rem' : '1.125rem',
                fontWeight: '700',
                color: '#1e40af',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
                Keyboard Shortcuts
              </h3>
              <button
                onClick={() => setShowTipBlock(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '0.75rem'
            }}>
              <div style={{
                background: 'white',
                borderRadius: '8px',
                padding: '0.75rem',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.25rem'
                }}>
                  <kbd style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    Ctrl
                  </kbd>
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>+</span>
                  <kbd style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    S
                  </kbd>
                </div>
                <div style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                  Submit Purchase
                </div>
              </div>
              <div style={{
                background: 'white',
                borderRadius: '8px',
                padding: '0.75rem',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.25rem'
                }}>
                  <kbd style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    Ctrl
                  </kbd>
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>+</span>
                  <kbd style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    A
                  </kbd>
                </div>
                <div style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                  Add New Row
                </div>
              </div>
              <div style={{
                background: 'white',
                borderRadius: '8px',
                padding: '0.75rem',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.25rem'
                }}>
                  <kbd style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    Ctrl
                  </kbd>
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>+</span>
                  <kbd style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    D
                  </kbd>
                </div>
                <div style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                  Delete Last Row
                </div>
              </div>
              <div style={{
                background: 'white',
                borderRadius: '8px',
                padding: '0.75rem',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.25rem',
                  flexWrap: 'wrap'
                }}>
                  <kbd style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    Ctrl
                  </kbd>
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>+</span>
                  <kbd style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    ←
                  </kbd>
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>/</span>
                  <kbd style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    →
                  </kbd>
                </div>
                <div style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                  Scroll Table Horizontally
                </div>
              </div>
            </div>
            <div style={{
              marginTop: '0.75rem',
              paddingTop: '0.75rem',
              borderTop: '1px solid #bfdbfe',
              fontSize: '0.75rem',
              color: '#6b7280',
              fontStyle: 'italic'
            }}>
              💡 Tip: These shortcuts work on desktop only
            </div>
          </div>
        )}

        {/* Add Row Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={handleAddRow}
            type="button"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: isMobile ? '0.75rem 1.25rem' : '0.875rem 1.5rem',
              fontSize: isMobile ? '0.875rem' : '0.9375rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              outline: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            <span>Add Row</span>
          </button>
          {rows.length > 0 && (
            <div style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              fontWeight: '500'
            }}>
              {rows.length} {rows.length === 1 ? 'item' : 'items'}
            </div>
          )}
        </div>

        {/* Mobile Card Layout */}
        {isMobile ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            width: '100%',
            maxWidth: '100%',
            padding: '0 0.5rem',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {rows.length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '3rem 1rem',
                textAlign: 'center',
                color: '#6b7280',
                border: '1px solid #e5e7eb'
              }}>
                <p style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '500', color: '#6b7280' }}>
                  No rows added yet
                </p>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af' }}>
                  Click "Add Row" to start adding purchase items
                </p>
              </div>
            ) : (
              rows.map((row, rowIndex) => {
                const isRemoving = removingRows.has(rowIndex);
                return (
                  <div
                    key={rowIndex}
                    className={isRemoving ? 'row-removing' : ''}
                    style={{
                      background: isRemoving ? '#fee2e2' : 'white',
                      borderRadius: '12px',
                      border: isRemoving ? '2px solid #ef4444' : '1px solid #e5e7eb',
                      padding: '1rem',
                      boxShadow: isRemoving ? '0 4px 12px rgba(239, 68, 68, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
                      position: 'relative',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      transition: 'all 0.2s ease',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Row Number Badge */}
                    <div style={{
                      position: 'absolute',
                      top: '0.75rem',
                      right: '0.75rem',
                      background: '#f3f4f6',
                      color: '#4b5563',
                      fontWeight: '600',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '6px',
                      fontSize: '0.75rem'
                    }}>
                      #{rowIndex + 1}
                    </div>

                    {/* Fields */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                      marginTop: '0.5rem'
                    }}>
                      {columns.map((column) => {
                        const value = row[column];
                        const isRightAligned = rightAlignColumns.includes(column);
                        const displayName = columnDisplayNames[column] || column;

                        const inputStyle: React.CSSProperties = {
                          width: '100%',
                          padding: '0.75rem 1rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          background: 'white',
                          color: '#111827',
                          outline: 'none',
                          transition: 'all 0.2s ease',
                          boxSizing: 'border-box',
                          margin: 0,
                          minHeight: '48px'
                        };

                        return (
                          <div
                            key={column}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.5rem 0',
                              borderBottom: '1px solid #f3f4f6'
                            }}
                          >
                            {/* Field Name */}
                            <div style={{
                              flex: '0 0 40%',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              color: '#6b7280',
                              textTransform: 'capitalize'
                            }}>
                              {displayName}:
                            </div>

                            {/* Field Value */}
                            <div style={{
                              flex: '1 1 60%',
                              display: 'flex',
                              justifyContent: isRightAligned ? 'flex-end' : 'flex-start'
                            }}>
                              {column === 'quantity_type' ? (
                                <select
                                  value={String(value)}
                                  onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                                  style={{
                                    ...inputStyle,
                                    cursor: 'pointer',
                                    background: 'white',
                                    width: '100%',
                                    maxWidth: '200px'
                                  }}
                                >
                                  {QUANTITY_TYPES.map((type) => (
                                    <option key={type} value={type}>
                                      {type}
                                    </option>
                                  ))}
                                </select>
                              ) : column === 'tablets_per_strip' ? (
                                (row.quantity_type === 'Strip' || row.quantity_type === 'Capsule') ? (
                                  <input
                                    type="number"
                                    value={row.tablets_per_strip === 0 ? '' : String(row.tablets_per_strip || 10)}
                                    onChange={(e) => handleCellChange(rowIndex, 'tablets_per_strip', parseInt(e.target.value) || 10)}
                                    placeholder="Tablets/Strip"
                                    min="1"
                                    style={{
                                      ...inputStyle,
                                      textAlign: isRightAligned ? 'right' : 'left',
                                      width: '100%',
                                      maxWidth: '200px'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#10b981';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#d1d5db';
                                      e.target.style.boxShadow = 'none';
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    color: '#9ca3af',
                                    fontSize: '0.875rem',
                                    fontStyle: 'italic',
                                    padding: '0.75rem 1rem',
                                    textAlign: 'center',
                                    width: '100%',
                                    maxWidth: '200px'
                                  }}>
                                    N/A
                                  </div>
                                )
                              ) : column === 'expiry_date' ? (
                                <input
                                  type="text"
                                  value={expiryDisplayValues[`${rowIndex}-expiry_date`] || convertToDateFormat(String(value))}
                                  onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                                  placeholder="MM-YYYY"
                                  maxLength={7}
                                  style={{
                                    ...inputStyle,
                                    background: 'white',
                                    width: '100%',
                                    maxWidth: '200px'
                                  }}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = '#10b981';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = '#d1d5db';
                                    e.target.style.boxShadow = 'none';
                                    // Normalize the date on blur
                                    const currentValue = e.target.value;
                                    if (currentValue.trim() !== '') {
                                      const normalized = parseAndNormalizeDate(currentValue);
                                      const updatedRows = [...rows];
                                      updatedRows[rowIndex] = { ...updatedRows[rowIndex], expiry_date: normalized };
                                      setRows(updatedRows);
                                      // Keep display as MM-YYYY format
                                      setExpiryDisplayValues(prev => ({
                                        ...prev,
                                        [`${rowIndex}-expiry_date`]: convertToDateFormat(normalized)
                                      }));
                                    }
                                  }}
                                />
                              ) : (column === 'quantity' || column === 'unit_cost' || 
                                    column === 'selling_price' || column === 'mrp') ? (
                                <input
                                  type="number"
                                  value={value === 0 ? '' : String(value)}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;
                                    if (inputValue === '') {
                                      handleCellChange(rowIndex, column, 0);
                                    } else {
                                      const parsedValue = parseFloat(inputValue);
                                      if (!isNaN(parsedValue)) {
                                        handleCellChange(rowIndex, column, parsedValue);
                                      }
                                    }
                                  }}
                                  placeholder={`Enter ${displayName}`}
                                  min="0"
                                  step={column === 'quantity' ? '1' : '0.01'}
                                  style={{
                                    ...inputStyle,
                                    textAlign: isRightAligned ? 'right' : 'left',
                                    width: '100%',
                                    maxWidth: '200px'
                                  }}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = '#10b981';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = '#d1d5db';
                                    e.target.style.boxShadow = 'none';
                                  }}
                                />
                              ) : column === 'medicine_name' ? (
                                <div style={{ 
                                  position: 'relative', 
                                  width: '100%',
                                  zIndex: (showDropdown[rowIndex] || isSearching[rowIndex]) ? 1000 : 1
                                }}>
                                  <input
                                    type="text"
                                    value={String(value)}
                                    onChange={(e) => handleMedicineNameChange(rowIndex, e.target.value)}
                                    placeholder="Enter Medicine Name"
                                    style={{
                                      ...inputStyle,
                                      textAlign: 'left',
                                      width: '100%',
                                      position: 'relative',
                                      zIndex: (showDropdown[rowIndex] || isSearching[rowIndex]) ? 10 : 1
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#10b981';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                      // Show dropdown if there are suggestions or if we're searching
                                      if ((medicineSuggestions[rowIndex] && medicineSuggestions[rowIndex].length > 0) || isSearching[rowIndex]) {
                                        setShowDropdown(prev => ({
                                          ...prev,
                                          [rowIndex]: true
                                        }));
                                        setActiveDropdownRow(rowIndex);
                                      }
                                    }}
                                    onBlur={(e) => {
                                      setTimeout(() => {
                                        if (e.target) {
                                          e.target.style.borderColor = '#d1d5db';
                                          e.target.style.boxShadow = 'none';
                                        }
                                      }, 200);
                                    }}
                                  />
                                </div>
                              ) : (
                                <input
                                  type="text"
                                  value={String(value)}
                                  onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                                  placeholder={`Enter ${displayName}`}
                                  style={{
                                    ...inputStyle,
                                    textAlign: isRightAligned ? 'right' : 'left',
                                    width: '100%'
                                  }}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = '#10b981';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = '#d1d5db';
                                    e.target.style.boxShadow = 'none';
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Dropdown - spans full card width */}
                    {activeDropdownRow === rowIndex && (showDropdown[rowIndex] || isSearching[rowIndex]) && (
                      <div
                        ref={(el) => { dropdownRefs.current[rowIndex] = el; }}
                        style={{
                          width: '100%',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          zIndex: 1001,
                          maxHeight: '270px',
                          overflowY: 'auto',
                          marginTop: '0.75rem'
                        }}
                      >
                        {isSearching[rowIndex] ? (
                          <div style={{
                            padding: '1.5rem',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '0.875rem'
                          }}>
                            Searching...
                          </div>
                        ) : medicineSuggestions[rowIndex] && medicineSuggestions[rowIndex].length > 0 ? (
                          medicineSuggestions[rowIndex].map((medicine, medIndex) => (
                            <div
                              key={medicine.key}
                              onClick={() => handleMedicineSelect(rowIndex, medicine)}
                              style={{
                                padding: '0.75rem 1rem',
                                cursor: 'pointer',
                                borderBottom: medIndex < medicineSuggestions[rowIndex].length - 1 ? '1px solid #f3f4f6' : 'none',
                                transition: 'background-color 0.15s ease',
                                background: 'white'
                              }}
                              onMouseEnter={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.backgroundColor = '#f0fdf4';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.backgroundColor = 'white';
                                }
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.75rem',
                                width: '100%'
                              }}>
                                {/* Medicine Image */}
                                {medicine.Image_URL && (
                                  <div 
                                    style={{
                                      flexShrink: 0,
                                      width: '50px',
                                      height: '50px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: '#f3f4f6',
                                      borderRadius: '4px',
                                      overflow: 'hidden'
                                    }}
                                  >
                                    <img 
                                      src={medicine.Image_URL} 
                                      alt={medicine.Name}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                      }}
                                    />
                                  </div>
                                )}
                                
                                {/* Medicine Details */}
                                <div style={{
                                  flex: 1,
                                  minWidth: 0
                                }}>
                                  <div style={{
                                    fontWeight: '600',
                                    color: '#1f2937',
                                    fontSize: '0.875rem',
                                    marginBottom: '0.25rem'
                                  }}>
                                    {medicine.Name}
                                  </div>
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: '#6b7280',
                                    marginBottom: '0.125rem'
                                  }}>
                                    <strong>Manufacturer:</strong> {medicine.Manufacturer}
                                  </div>
                                  <div style={{
                                    fontSize: '0.75rem',
                                    color: '#6b7280',
                                    marginBottom: medicine.Quantity ? '0.125rem' : '0'
                                  }}>
                                    <strong>Composition:</strong> {medicine.Salt_Composition}
                                  </div>
                                  {medicine.Quantity && (
                                    <div style={{
                                      fontSize: '0.75rem',
                                      color: '#6b7280'
                                    }}>
                                      <strong>Quantity:</strong> {medicine.Quantity}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{
                            padding: '1.5rem',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '0.875rem'
                          }}>
                            No medicines found
                          </div>
                        )}
                      </div>
                    )}

                    {/* Remove Button */}
                    <div style={{
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid #e5e7eb',
                      display: 'flex',
                      justifyContent: 'center'
                    }}>
                      <button
                        onClick={() => handleRemoveRow(rowIndex)}
                        style={{
                          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '0.625rem 1.25rem',
                          fontSize: '0.8125rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)',
                          width: '100%'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          <line x1="10" y1="11" x2="10" y2="17"/>
                          <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                        Remove Item
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          /* Desktop Table Layout */
          <div 
            ref={(el) => {
              tableContainerRef.current = el;
              tableScrollableRef.current = el;
            }}
            className="quick-purchase-table-scroll-container"
            style={{
              border: '2px solid #e5e7eb',
              borderRadius: '0',
              overflowX: 'auto',
              overflowY: 'visible',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
              background: 'white',
              margin: 0,
              padding: 0,
              width: '100%',
              maxWidth: '100%',
              minHeight: 'fit-content',
              position: 'relative'
            }}
          >
            <div 
              style={{
                overflowX: 'visible',
                overflowY: 'visible',
                margin: 0,
                padding: 0,
                width: '100%',
                minHeight: 'fit-content'
              }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.875rem',
                margin: 0,
                padding: 0,
                borderSpacing: 0,
                minWidth: '100%'
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                    borderBottom: '2px solid #d1d5db'
                  }}>
                    {columns.map((column) => {
                      const displayName = columnDisplayNames[column] || column;
                      const isRightAligned = rightAlignColumns.includes(column);
                      
                      return (
                        <th
                          key={column}
                          style={{
                            padding: '1rem 0.75rem',
                            textAlign: isRightAligned ? 'right' : 'left',
                            fontWeight: '600',
                            color: '#374151',
                            fontSize: '0.8125rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                            whiteSpace: 'nowrap',
                            minWidth: '120px',
                            margin: 0
                          }}
                        >
                          {displayName}
                        </th>
                      );
                    })}
                    <th
                      style={{
                        padding: '1rem 0.75rem',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: '#374151',
                        fontSize: '0.8125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                        whiteSpace: 'nowrap',
                        minWidth: '100px'
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        style={{
                          padding: '3rem 1rem',
                          textAlign: 'center',
                          color: '#6b7280'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.75rem'
                        }}>
                          <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: '#6b7280' }}>
                            No rows added yet
                          </p>
                          <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af' }}>
                            Click "Add Row" to start adding purchase items
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, rowIndex) => {
                      const isRemoving = removingRows.has(rowIndex);
                      return (
                        <React.Fragment key={rowIndex}>
                        <tr
                          key={rowIndex}
                          className={isRemoving ? 'row-removing' : ''}
                          style={{
                            borderBottom: '1px solid #e5e7eb',
                            background: isRemoving ? '#fee2e2' : (rowIndex % 2 === 1 ? '#f9fafb' : 'white'),
                            transition: 'background-color 0.15s ease',
                            position: 'relative',
                            borderLeft: isRemoving ? '3px solid #ef4444' : 'none'
                          }}
                          ref={(el) => { if (el && activeDropdownRow === rowIndex) dropdownRefs.current[rowIndex] = el; }}
                          onMouseEnter={(e) => {
                            if (!isRemoving && e.currentTarget) {
                              e.currentTarget.style.backgroundColor = '#ecfdf5';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isRemoving && e.currentTarget) {
                              e.currentTarget.style.backgroundColor = rowIndex % 2 === 1 ? '#f9fafb' : 'white';
                            }
                          }}
                        >
                          {columns.map((column, colIndex) => {
                            const value = row[column];
                            const isRightAligned = rightAlignColumns.includes(column);

                            const inputStyle: React.CSSProperties = {
                              width: '100%',
                              padding: '0.75rem 1rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              fontSize: '0.875rem',
                              background: 'white',
                              color: '#111827',
                              outline: 'none',
                              transition: 'all 0.2s ease',
                              minWidth: '120px',
                              boxSizing: 'border-box',
                              margin: 0,
                              minHeight: '48px'
                            };

                            return (
                              <td
                                key={column}
                                style={{
                                  padding: '0.75rem',
                                  textAlign: isRightAligned ? 'right' : 'left',
                                  borderRight: '1px solid #f3f4f6',
                                  verticalAlign: 'top',
                                  margin: 0
                                }}
                              >
                                {column === 'quantity_type' ? (
                                  <select
                                    value={value}
                                    onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                                    style={{
                                      ...inputStyle,
                                      cursor: 'pointer',
                                      background: 'white'
                                    }}
                                  >
                                    {QUANTITY_TYPES.map((type) => (
                                      <option key={type} value={type}>
                                        {type}
                                      </option>
                                    ))}
                                  </select>
                                ) : column === 'tablets_per_strip' ? (
                                  (row.quantity_type === 'Strip' || row.quantity_type === 'Capsule') ? (
                                    <input
                                      type="number"
                                      value={row.tablets_per_strip === 0 ? '' : String(row.tablets_per_strip || 10)}
                                      onChange={(e) => handleCellChange(rowIndex, 'tablets_per_strip', parseInt(e.target.value) || 10)}
                                      placeholder="Tablets/Strip"
                                      min="1"
                                      style={{
                                        ...inputStyle,
                                        textAlign: isRightAligned ? 'right' : 'left'
                                      }}
                                      onFocus={(e) => {
                                        e.target.style.borderColor = '#10b981';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                      }}
                                      onBlur={(e) => {
                                        e.target.style.borderColor = '#d1d5db';
                                        e.target.style.boxShadow = 'none';
                                      }}
                                    />
                                  ) : (
                                    <div style={{
                                      color: '#9ca3af',
                                      fontSize: '0.875rem',
                                      fontStyle: 'italic',
                                      padding: '0.75rem 1rem',
                                      textAlign: 'center'
                                    }}>
                                      N/A
                                    </div>
                                  )
                                ) : column === 'expiry_date' ? (
                                  <input
                                    type="text"
                                    value={expiryDisplayValues[`${rowIndex}-expiry_date`] || convertToDateFormat(String(value))}
                                    onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                                    placeholder="MM-YYYY"
                                    maxLength={7}
                                    style={{
                                      ...inputStyle,
                                      minWidth: '150px',
                                      background: 'white'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#10b981';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#d1d5db';
                                      e.target.style.boxShadow = 'none';
                                      // Normalize the date on blur
                                      const currentValue = e.target.value;
                                      if (currentValue.trim() !== '') {
                                        const normalized = parseAndNormalizeDate(currentValue);
                                        const updatedRows = [...rows];
                                        updatedRows[rowIndex] = { ...updatedRows[rowIndex], expiry_date: normalized };
                                        setRows(updatedRows);
                                        // Keep display as MM-YYYY format
                                        setExpiryDisplayValues(prev => ({
                                          ...prev,
                                          [`${rowIndex}-expiry_date`]: convertToDateFormat(normalized)
                                        }));
                                      }
                                    }}
                                  />
                                ) : (column === 'quantity' || column === 'unit_cost' || 
                                      column === 'selling_price' || column === 'mrp') ? (
                                  <input
                                    type="number"
                                    value={value === 0 ? '' : String(value)}
                                    onChange={(e) => {
                                      const inputValue = e.target.value;
                                      if (inputValue === '') {
                                        handleCellChange(rowIndex, column, 0);
                                      } else {
                                        const parsedValue = parseFloat(inputValue);
                                        if (!isNaN(parsedValue)) {
                                          handleCellChange(rowIndex, column, parsedValue);
                                        }
                                      }
                                    }}
                                    placeholder={`Enter ${columnDisplayNames[column] || column}`}
                                    min="0"
                                    step={column === 'quantity' ? '1' : '0.01'}
                                    style={{
                                      ...inputStyle,
                                      textAlign: isRightAligned ? 'right' : 'left'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#10b981';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#d1d5db';
                                      e.target.style.boxShadow = 'none';
                                    }}
                                  />
                                ) : column === 'medicine_name' ? (
                                  <div style={{ 
                                    position: 'relative', 
                                    width: '100%',
                                    zIndex: (showDropdown[rowIndex] || isSearching[rowIndex]) ? 1000 : 1
                                  }} ref={(el) => { dropdownRefs.current[rowIndex] = el; }}>
                                    <input
                                      type="text"
                                      value={String(value)}
                                      onChange={(e) => handleMedicineNameChange(rowIndex, e.target.value)}
                                      placeholder="Enter Medicine Name"
                                      style={{
                                        ...inputStyle,
                                        position: 'relative',
                                        zIndex: (showDropdown[rowIndex] || isSearching[rowIndex]) ? 10 : 1
                                      }}
                                      onFocus={(e) => {
                                        e.target.style.borderColor = '#10b981';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                        // Show dropdown if there are suggestions or if we're searching
                                        if ((medicineSuggestions[rowIndex] && medicineSuggestions[rowIndex].length > 0) || isSearching[rowIndex]) {
                                          setShowDropdown(prev => ({
                                            ...prev,
                                            [rowIndex]: true
                                          }));
                                          setActiveDropdownRow(rowIndex);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        setTimeout(() => {
                                          if (e.target) {
                                            e.target.style.borderColor = '#d1d5db';
                                            e.target.style.boxShadow = 'none';
                                          }
                                        }, 200);
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <input
                                    type="text"
                                    value={String(value)}
                                    onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
                                    placeholder={`Enter ${columnDisplayNames[column] || column}`}
                                    style={{
                                      ...inputStyle
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#10b981';
                                      e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#d1d5db';
                                      e.target.style.boxShadow = 'none';
                                    }}
                                  />
                                )}
                              </td>
                            );
                          })}
                          {/* Actions Column */}
                          <td
                            style={{
                              padding: '0.75rem',
                              textAlign: 'center',
                              borderRight: '1px solid #f3f4f6',
                              verticalAlign: 'middle'
                            }}
                          >
                            <button
                              onClick={() => handleRemoveRow(rowIndex)}
                              style={{
                                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '0.5rem 0.75rem',
                                fontSize: '0.8125rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.375rem',
                                boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)',
                                minWidth: '90px',
                                margin: '0 auto'
                              }}
                              onMouseEnter={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (e.currentTarget) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.2)';
                                }
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                <line x1="10" y1="11" x2="10" y2="17"/>
                                <line x1="14" y1="11" x2="14" y2="17"/>
                              </svg>
                              Remove
                            </button>
                          </td>
                        </tr>
                        {/* Dropdown row - spans full width */}
                        {activeDropdownRow === rowIndex && (showDropdown[rowIndex] || isSearching[rowIndex]) && (
                          <tr
                            key={`dropdown-${rowIndex}`}
                            style={{
                              position: 'relative',
                              zIndex: 1001
                            }}
                          >
                            <td
                              colSpan={columns.length + 1}
                              style={{
                                padding: '0.75rem',
                                position: 'relative'
                              }}
                            >
                              <div
                                ref={(el) => { if (el) dropdownRefs.current[rowIndex] = el; }}
                                style={{
                                  width: '100%',
                                  background: 'white',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                  zIndex: 1001,
                                  maxHeight: '270px',
                                  overflowY: 'auto'
                                }}
                              >
                                {isSearching[rowIndex] ? (
                                  <div style={{
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    color: '#6b7280',
                                    fontSize: '0.875rem'
                                  }}>
                                    Searching...
                                  </div>
                                ) : medicineSuggestions[rowIndex] && medicineSuggestions[rowIndex].length > 0 ? (
                                  medicineSuggestions[rowIndex].map((medicine, medIndex) => (
                                    <div
                                      key={medicine.key}
                                      onClick={() => handleMedicineSelect(rowIndex, medicine)}
                                      style={{
                                        padding: '0.75rem 1rem',
                                        cursor: 'pointer',
                                        borderBottom: medIndex < medicineSuggestions[rowIndex].length - 1 ? '1px solid #f3f4f6' : 'none',
                                        transition: 'background-color 0.15s ease',
                                        background: 'white'
                                      }}
                                      onMouseEnter={(e) => {
                                        if (e.currentTarget) {
                                          e.currentTarget.style.backgroundColor = '#f0fdf4';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (e.currentTarget) {
                                          e.currentTarget.style.backgroundColor = 'white';
                                        }
                                      }}
                                    >
                                      <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '0.75rem',
                                        width: '100%'
                                      }}>
                                        {/* Medicine Image */}
                                        {medicine.Image_URL && (
                                          <div 
                                            style={{
                                              flexShrink: 0,
                                              width: '50px',
                                              height: '50px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              background: '#f3f4f6',
                                              borderRadius: '4px',
                                              overflow: 'hidden'
                                            }}
                                          >
                                            <img 
                                              src={medicine.Image_URL} 
                                              alt={medicine.Name}
                                              style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                              }}
                                              onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                              }}
                                            />
                                          </div>
                                        )}
                                        
                                        {/* Medicine Details */}
                                        <div style={{
                                          flex: 1,
                                          minWidth: 0
                                        }}>
                                          <div style={{
                                            fontWeight: '600',
                                            color: '#1f2937',
                                            fontSize: '0.875rem',
                                            marginBottom: '0.25rem'
                                          }}>
                                            {medicine.Name}
                                          </div>
                                          <div style={{
                                            fontSize: '0.75rem',
                                            color: '#6b7280',
                                            marginBottom: '0.125rem'
                                          }}>
                                            <strong>Manufacturer:</strong> {medicine.Manufacturer}
                                          </div>
                                          <div style={{
                                            fontSize: '0.75rem',
                                            color: '#6b7280',
                                            marginBottom: medicine.Quantity ? '0.125rem' : '0'
                                          }}>
                                            <strong>Composition:</strong> {medicine.Salt_Composition}
                                          </div>
                                          {medicine.Quantity && (
                                            <div style={{
                                              fontSize: '0.75rem',
                                              color: '#6b7280'
                                            }}>
                                              <strong>Quantity:</strong> {medicine.Quantity}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div style={{
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    color: '#6b7280',
                                    fontSize: '0.875rem'
                                  }}>
                                    No medicines found
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Summary and Action Buttons */}
        {rows.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'space-between',
            alignItems: 'center',
            paddingTop: '1.5rem',
            marginTop: '1.5rem',
            borderTop: '2px solid #e5e7eb',
            flexWrap: 'wrap',
            gap: '1rem',
            width: '100%'
          }}>
            {/* Summary Section */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.25rem',
              width: isMobile ? '100%' : 'auto',
              alignItems: isMobile ? 'center' : 'flex-start'
            }}>
              <div style={{
                fontSize: isMobile ? '0.875rem' : '0.9375rem',
                fontWeight: '600',
                color: '#374151'
              }}>
                Total Items: <span style={{ color: '#10b981', fontWeight: '700' }}>{rows.length}</span>
              </div>
              <div style={{
                fontSize: isMobile ? '0.875rem' : '0.9375rem',
                fontWeight: '600',
                color: '#374151'
              }}>
                Total Value: <span style={{ color: '#10b981', fontWeight: '700' }}>{formatCurrency(calculateTotalValue())}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '0.75rem',
              width: isMobile ? '100%' : 'auto'
            }}>
              <button
                onClick={() => {
                  setRows([createEmptyRow()]);
                }}
                type="button"
                disabled={isSubmitting}
                style={{
                  background: 'transparent',
                  color: '#ef4444',
                  border: '2px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '8px',
                  padding: isMobile ? '0.75rem 1.5rem' : '0.75rem 1.5rem',
                  fontSize: isMobile ? '0.875rem' : '0.9375rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  justifyContent: 'center',
                  width: isMobile ? '100%' : 'auto',
                  opacity: isSubmitting ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting && e.currentTarget) {
                    e.currentTarget.style.background = '#fef2f2';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
                <span>Clear All</span>
              </button>
              <button
                onClick={handleSubmit}
                type="button"
                disabled={isSubmitting}
                style={{
                  background: isSubmitting 
                    ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: isMobile ? '0.75rem 1.5rem' : '0.875rem 2rem',
                  fontSize: isMobile ? '0.875rem' : '0.9375rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: isSubmitting 
                    ? '0 2px 4px rgba(156, 163, 175, 0.2)'
                    : '0 4px 12px rgba(16, 185, 129, 0.3)',
                  outline: 'none',
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: 'center',
                  opacity: isSubmitting ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting && e.currentTarget) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting && e.currentTarget) {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>Submit Purchase</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideDownFromTop {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes rowRemove {
          0% {
            opacity: 1;
            transform: translateX(0);
            max-height: 1000px;
          }
          100% {
            opacity: 0;
            transform: translateX(-100%);
            max-height: 0;
            margin: 0;
            padding: 0;
          }
        }
        .row-removing {
          animation: rowRemove 0.3s ease-out forwards !important;
          overflow: hidden;
        }
        .row-removing * {
          pointer-events: none;
        }
        /* Thin modern horizontal scrollbar (overrides index.css global hide) */
        .quick-purchase-table-scroll-container {
          -ms-overflow-style: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(113, 113, 122, 0.55) rgba(24, 24, 27, 0.06);
        }
        .quick-purchase-table-scroll-container::-webkit-scrollbar {
          display: block;
          height: 6px;
          -webkit-appearance: none;
        }
        .quick-purchase-table-scroll-container::-webkit-scrollbar-track {
          background: rgba(24, 24, 27, 0.06);
          border-radius: 100px;
          margin: 0 6px;
        }
        .quick-purchase-table-scroll-container::-webkit-scrollbar-thumb {
          background: rgba(113, 113, 122, 0.45);
          border-radius: 100px;
          border: 2px solid transparent;
          background-clip: padding-box;
          transition: background-color 0.15s ease;
          min-width: 40px;
        }
        .quick-purchase-table-scroll-container::-webkit-scrollbar-thumb:hover {
          background-color: rgba(82, 82, 91, 0.6);
          background-clip: padding-box;
        }
        .quick-purchase-table-scroll-container::-webkit-scrollbar-thumb:active {
          background-color: rgba(63, 63, 70, 0.75);
          background-clip: padding-box;
        }
        .quick-purchase-table-scroll-container::-webkit-scrollbar-corner {
          background: transparent;
        }
      `}</style>
    </>
  );
};

export default QuickPurchase;
